import { Box, IconButton, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { X } from "react-feather";
import { Badge, Button, Label } from "reactstrap";
import { useDebounce } from "../../../../Hooks/useDebounce";
import { faEye, faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  deleteFormMapping,
  getFormDataMapping,
} from "../../../../store/slices/formBuilder";
import { useSelector } from "react-redux";
import { getAllPrograms } from "../../../../store/slices/programsSlice";
import { findOptionSelect } from "./CreateFormMappingPage";
import Swal from "sweetalert2";
import Select from "react-select";

const ServiceFormPlanMapping = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedData = location.state?.data || {};
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const tenantClientData = useSelector((state) => state.clients);
  const [formFields, setFormFields] = useState({
    program_slug: null,
    service_slug: null,
    sub_service_slug: null,
    category_slug: null,
    sub_category_slug: null,
  });
  const [showFilters, setShowFilters] = useState(false);

  const [allPrograms, setAllPrograms] = useState([]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "Sr", // Virtual column
        header: "S.No",
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => {
          return (page - 1) * pageSize + row.index + 1;
        },
      },
      {
        accessorKey: "program_name",
        header: "Program",
        size: 250,
        enableSorting: false,
      },
      {
        accessorKey: "service_name",
        header: "service",
        size: 250,
        enableSorting: false,
      },
      {
        accessorKey: "sub_service_name",
        header: "Sub Service",
        size: 250,
        enableSorting: false,
      },
      {
        accessorKey: "category_name",
        header: "Category",
        size: 250,
        enableSorting: false,
      },
      {
        accessorKey: "sub_category_name",
        header: "Sub Category",
        size: 250,
        enableSorting: false,
      },
      {
        accessorKey: "action",
        header: "Actions",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              outline
              color="info"
              size="sm"
              onClick={() => {
                navigate(
                  `${process.env.PUBLIC_URL}/pages/form-management/mapping/${selectedData?.ID}/view/${row.original.ID}`,
                  { state: { formData: selectedData, mappingData: row.original } }
                );
              }}
              title="View Form"
            >
              <FontAwesomeIcon data-action-type="view" icon={faEye} />
            </Button>
            <Button
              outline
              color="primary"
              size="sm"
              onClick={() => {
                navigate(
                  `${process.env.PUBLIC_URL}/pages/form-management/mapping/${selectedData?.ID}/edit/${row.original.ID}`,
                  { state: { formData: selectedData, mappingData: row.original } }
                );
              }}
              title="Edit Form"
            >
              <FontAwesomeIcon data-action-type="edit" icon={faPencil} />
            </Button>
            <Button
              outline
              color="danger"
              size="sm"
              onClick={() => {
                onDelete(row?.original?.ID);
                // rowAction("DELETE", row?.original);
              }}
              title="Delete Form"
            >
              <FontAwesomeIcon data-action-type="delete" icon={faTrash} />
            </Button>
          </div>
        ),
      },
    ],
    [data],
  );

  const fetchData = async (searchQuery) => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        getFormDataMapping({
          limit: pageSize,
          page: page,
          form_id: selectedData?.ID,
          searchQuery: searchQuery,
          getByID: true,
          ...formFields
        }),
      );
      console.log("response.payload.", response.payload);
      if (response?.payload?.data) {
        setData(response?.payload?.data || []);
        setTotalDataCount(response?.payload?.total);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to fetch users.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      fetchData(debouncedSearch);
    } else {
      fetchData();
    }
  }, [page, pageSize, searchQuery, debouncedSearch]);

  const handlePageChange = (updater) => {
    // updater can be a function or an object from MRT
    const nextState =
      typeof updater === "function"
        ? updater({ pageIndex: page - 1, pageSize: pageSize })
        : updater;

    // Check if pageSize actually changed
    if (nextState.pageSize !== pageSize) {
      setPageSize(nextState.pageSize);
      setPage(1); // Reset to first page if limit changes
    } else {
      setPage(nextState.pageIndex + 1); // Convert 0-based back to 1-based
    }
  };

  const handleOnChangeSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const table = useMaterialReactTable({
    columns,
    data,
    initialState: { density: "compact" },
    enableStickyHeader: true,
    manualPagination: true,
    rowCount: totalDataCount,
    onPaginationChange: handlePageChange,
    state: {
      pagination: { pageIndex: page, pageSize: pageSize },
      isLoading: isLoading,
    },
    muiSkeletonProps: {
      animation: "wave",
      height: 28, // Set your desired height in pixels
      sx: {
        borderRadius: "4px", // Optional: match your table's design
      },
    },
    enableColumnFilters: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableGlobalFilter: false,
    // 🗂 allow column visibility toggle menu
    enableColumnVisibility: false,
    enableColumnVisibilityButton: false,
    enableHiding: false,
    enableTopToolbar: false,
    enableTableHeader: false,
  });

  const fetchPrograms = async () => {
    const result = await dispatch(
      getAllPrograms({
        pagination: false,
        getServiceData: true,
        getSubServiceData: true,
        getFilteredData: true,
      }),
    );

    const formatData = result?.payload?.map((program) => ({
      ...program,
      label: program?.program_name,
      value: program?.slug_name,
    }));

    if (Array.isArray(result.payload)) {
      setAllPrograms(formatData);
    }
  };

  useEffect(() => {
    fetchPrograms();
    // dispatch(getAllServices({ pagination: false }));
    // dispatch(getAllSubServices({ pagination: false }));
    // fetchCategory();
    // fetchSubCategory();
  }, []);



  const onDelete = async (ID) => {
    const { isConfirmed } = await Swal.fire({
      title: "Delete this mapping?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: () => {
        const container = Swal.getContainer(); // <div class="swal2-container">
        if (container) container.style.zIndex = String(999999);
      },

      preConfirm: async () => {
        try {
          // run the thunk and bubble up errors to SweetAlert
          const res = await dispatch(deleteFormMapping({ ID })).unwrap();
          return res;
        } catch (err) {
          const message =
            err?.message ||
            err?.error ||
            err?.data?.message ||
            "Unable to delete mapping.";
          // Show validation message inside the dialog (below buttons)
          Swal.showValidationMessage(message);
          throw err; // also reject preConfirm
        }
      },
    });

    if (!isConfirmed) return;

    await Swal.fire({
      icon: "success",
      title: "Mapping deleted",
      text: "Your form mapping has been deleted successfully.",
      confirmButtonText: "OK",
      timer: 1600,
      timerProgressBar: true,
      didOpen: () => {
        const container = Swal.getContainer(); // <div class="swal2-container">
        if (container) container.style.zIndex = String(999999);
      },
    });

    if (fetchData) fetchData();
  };

  /** Cascading filters via *_slug keys */
  const filteredServices = useMemo(() => {
    if (!formFields.program_slug) return [];
    const result =
      allPrograms
        .find((s) => s?.slug_name === formFields.program_slug)
        ?.services?.map((select) => ({
          ...select,
          label: select?.service_name,
          value: select?.slug_name,
        })) || [];

    return result;
  }, [allPrograms, formFields.program_slug]);

  const filteredSubServices = useMemo(() => {
    if (!formFields.service_slug) return [];
    const result =
      filteredServices
        ?.find((s) => s?.slug_name === formFields?.service_slug)
        ?.sub_services?.map((select) => ({
          ...select,
          label: select?.sub_service_name,
          value: select?.slug_name,
        })) || [];

    return result;
  }, [filteredServices, formFields.service_slug]);

  const filteredCategories = useMemo(() => {
    if (!formFields.category_slug) return [];
    const result =
      filteredSubServices
        ?.find((s) => s?.slug_name === formFields?.sub_service_slug)
        ?.categories?.map((select) => ({
          ...select,
          label: select?.category_name,
          value: select?.slug_name,
        })) || [];

    return result;
  }, [filteredSubServices, formFields.category_slug]);

  const filteredSubCategories = useMemo(() => {
    if (!formFields.category_slug) return [];
    const result =
      filteredCategories
        ?.find((s) => s?.slug_name === formFields?.category_slug)
        ?.sub_categories?.map((select) => ({
          ...select,
          label: select?.sub_category_name,
          value: select?.slug_name,
        })) || [];

    return result;
  }, [filteredCategories, formFields.category_slug]);

  const handleSelect = (option, key) => {
    setFormFields((prev) => {
      const next = { ...prev, [key]: option?.value ?? null };
      // Reset dependents on parent change
      if (key === "program_slug") {
        next.service_slug = null;
        next.sub_service_slug = null;
      }
      if (key === "service_slug") {
        next.sub_service_slug = null;
      }
      if (key === "category_slug") {
        next.sub_category_slug = null;
      }
      return next;
    });
    // setErrors((e) => ({ ...e, [key]: undefined }));
  };


  const handleFilter = ()=>{
      setPage(1)
      fetchData()
  }
  const handleFilterClear = ()=>{
    setFormFields((preve)=>{
      return{
        ...preve,
        program_slug: null,
        service_slug: null,
        sub_service_slug: null,
        category_slug: null,
        sub_category_slug: null,
      }
    })
    fetchData()
  }

  return (
    <div className="container-fluid py-3" style={{ display: "flex", flexDirection: "column",backgroundColor: "#fff", minHeight: "80%" }}>
      {/* Header row with title + Close button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          // borderBottom: "1px solid",
          // borderColor: "divider",
        }}
      >
        <Typography variant="h6" component="h2">
          {selectedData?.name ?? "-"}
        </Typography>

        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <Button
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>

          <Button
            color="primary"
            onClick={() =>
              navigate(
                `${process.env.PUBLIC_URL}/pages/form-management/mapping/${selectedData?.ID}/create`,
                { state: { formData: selectedData } }
              )
            }
          >
            Create Form Mapping
          </Button>

          <IconButton
            edge="end"
            aria-label="Close"
            onClick={() => navigate(-1)}
            size="small"
          >
            <X />
          </IconButton>
        </div>
      </Box>

      {showFilters && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            width: "100%",
          gap: "1rem",
          px: 2,
          py: 1.5,
        }}
      >
        <div>
          <Label htmlFor="Program">Program</Label>
          <Select
            id="Program"
            options={allPrograms}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            value={findOptionSelect(allPrograms, formFields.program_slug)}
            onChange={(opt) => handleSelect(opt, "program_slug")}
            // className="js-example-basic-single col-sm-12"
            // onChange={(e) => handleSelect(e, "service_id")}
            // isDisabled={false}
            // value={formFields.service_id}
            // {...formFieldsValidation.service_id}
          />
        </div>
        <div>
          <Label htmlFor="Service">Service</Label>
          <Select
            id="Service"
            options={filteredServices}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            value={findOptionSelect(filteredServices, formFields.service_slug)}
            onChange={(opt) => handleSelect(opt, "service_slug")}

            // className="js-example-basic-single col-sm-12"
            // onChange={(e) => handleSelect(e, "service_id")}
            // isDisabled={false}
            // value={formFields.service_id}
            // {...formFieldsValidation.service_id}
          />
        </div>
        <div>
          <Label htmlFor="sub_service_slug">Sub Service</Label>
          <Select
            id="sub_service_slug"
            options={filteredSubServices}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            value={findOptionSelect(
              filteredSubServices,
              formFields.sub_service_slug,
            )}
            onChange={(opt) => handleSelect(opt, "sub_service_slug")}
            // className="js-example-basic-single col-sm-12"
            // onChange={(e) => handleSelect(e, "service_id")}
            // isDisabled={false}
            // value={formFields.service_id}
            // {...formFieldsValidation.service_id}
          />
        </div>
        <div>
          <Label htmlFor="category_slug">Category</Label>
          <Select
            id="category_slug"
            options={filteredCategories}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            value={findOptionSelect(
              filteredCategories,
              formFields.category_slug,
            )}
            onChange={(opt) => handleSelect(opt, "category_slug")}
            // className="js-example-basic-single col-sm-12"
            // onChange={(e) => handleSelect(e, "service_id")}
            // isDisabled={false}
            // value={formFields.service_id}
            // {...formFieldsValidation.service_id}
          />
        </div>
        <div>
          <Label htmlFor="sub_category_slug">Sub Category</Label>
          <Select
            id="sub_category_slug"
            options={filteredSubCategories}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
            value={findOptionSelect(
              filteredSubCategories,
              formFields.sub_category_slug,
            )}
            onChange={(opt) => handleSelect(opt, "sub_category_slug")}
            // onChange={(e) => handleSelect(e, "service_id")}
            // isDisabled={false}
            // value={formFields.service_id}
            // {...formFieldsValidation.service_id}
          />
        </div>
        <div
          style={{
            display: "grid",
            alignItems: "end",
          }}
        >
          <div className="py-1"></div>
          <div 
           className="d-flex gap-4"
          >
            <Button
            color="primary"
            style={{
              width: "100%",
            }}
            onClick={handleFilter}
          >
            Filter
          </Button>
           <Button
            color="primary"
            style={{
              width: "100%",
            }}
            outline
            onClick={handleFilterClear}
          >
            Clear Filter
          </Button>
          </div>
         
        </div>
        </Box>
      )}

      {/* Drawer body */}
      <Box sx={{ p: 2 }}>
        <div>
          {/* <Container> */}
          {/* <Card> */}
          <MaterialReactTable table={table} />
          {/* </Card> */}
          {/* </Container> */}
        </div>
      </Box>
    </div>
  );
};

export default ServiceFormPlanMapping;
